import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EditorContent,
  Node,
  mergeAttributes,
  nodeInputRule,
  useEditor,
  useEditorState,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { toast } from 'sonner';
import {
  Bold as BoldIcon,
  ImagePlus,
  Italic as ItalicIcon,
  Link2,
  Link2Off,
  List,
  ListOrdered,
  Plus,
  Underline as UnderlineIcon,
} from 'lucide-react';
import {
  MERGE_VARIABLES,
  MERGE_VARIABLE_LABELS,
  MESSAGE_BODY_MAX_IMAGES,
  MESSAGE_BODY_MAX_IMAGE_BYTES,
  plainTextToHtml,
} from '@vendorops/shared';
import { ImageRejected, prepareImageForEmail } from '@/lib/image-downscale';
import { variableChipClass, variableLabel } from '@/components/VariableField';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

/**
 * The message body editor: where a planner writes the email a vendor receives.
 *
 * Two things make this different from the signature editor next door, and both
 * come from the same source — a body is *written here*, where a signature is
 * usually pasted in from Gmail.
 *
 *  - **Merge variables are nodes, not text.** `{{vendor_name}}` is punctuation
 *    a planner should never have to type correctly. It is a chip: one object,
 *    selected and deleted as a unit, impossible to half-delete into
 *    `{{vendor_name}` — which is the typo the send endpoint refuses and the
 *    planner cannot see. It serializes back to the literal token, so the merge
 *    and the server's validation are unchanged by any of this.
 *  - **Images are dropped in.** The whole point of the feature: a look-book
 *    goes where the cursor is, not into an attachment list. They are resized in
 *    the browser (`lib/image-downscale.ts`), uploaded as bytes we own, and
 *    turned into `cid:` parts at send — never a remote URL, which would be a
 *    read receipt in a vendor's inbox.
 *
 * The schema stays a strict subset of `backend/src/lib/body-sanitize.ts`'s
 * allowlist. That server sanitizer is the boundary and it discards silently, so
 * anything this editor can build that it strips would appear to work and then
 * evaporate a page-load later — the one failure a planner cannot diagnose.
 */

const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

/** `{{opening}}` is assembled by the composer rather than merged, so it is
 *  offered alongside the real variables but is not one of them. */
const OPENING = 'opening';

/** What the palette offers, in the order it offers it. The opening line sits
 *  last because it is the one entry that is not a merge variable. */
const PALETTE: { name: string; label: string }[] = [
  ...MERGE_VARIABLES.map((name) => ({ name, label: MERGE_VARIABLE_LABELS[name] })),
  { name: OPENING, label: 'Opening line' },
];

/** The bubble, in both places it is drawn: the serialized span (where the
 *  class is stripped server-side) and the node view the planner actually sees. */
/**
 * `inline`, deliberately, and not `inline-flex`.
 *
 * An inline-level *box* contributes its padding to the line box, so every line
 * holding a chip stood taller than the lines around it and a paragraph of
 * merged prose rippled. Padding on a true inline box paints without pushing
 * the line apart, which is the whole trick: the pill is drawn around the text
 * and the text keeps the body's own rhythm. `box-decoration-break` is what
 * keeps both ends rounded when a long value wraps across two lines.
 */
const CHIP_CLASS =
  'inline rounded-full px-2 py-0.5 align-baseline text-[0.9em] font-medium [box-decoration-break:clone] [-webkit-box-decoration-break:clone]';

/**
 * A merge variable as a single indivisible object.
 *
 * `renderHTML` emits the token as the span's text, so `getHTML()` — the string
 * that is stored, merged, and validated — contains `{{vendor_name}}` exactly as
 * it always did. Nothing downstream knows this node exists. The
 * `data-variable` attribute is what lets the chip come *back* when the body is
 * reopened; the sanitizer keeps that one attribute for precisely this reason.
 */
const MergeVariable = Node.create<{ resolve?: (name: string) => string | undefined }>({
  name: 'mergeVariable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return {
      /** What this wedding already knows about a variable, for the chip to
       *  show instead of its own name. See the node view below. */
      resolve: undefined,
    };
  },

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-variable'),
        renderHTML: (attributes: Record<string, unknown>) => ({
          'data-variable': attributes.name as string,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        // Editor-only chrome: `class` is not in the sanitizer's allowlist, so
        // it is dropped server-side and never reaches a vendor's client.
        class: cn(CHIP_CLASS, variableChipClass(node.attrs.name as string)),
      }),
      `{{${node.attrs.name}}}`,
    ];
  },

  /**
   * What the chip *looks* like, which is not what it serializes to.
   *
   * `renderHTML` above is the serializer — `getHTML()` runs through it, and it
   * has to keep emitting the literal `{{token}}` the merge and the send
   * endpoint's validation are written against. A node view overrides the
   * on-screen rendering only, so the braces can come off the bubble without
   * coming out of the stored body. A planner reading their own draft should see
   * the value that will land there, not the punctuation that fetches it; the
   * bubble is already the signal that this is a drop-in rather than prose.
   *
   * Which is why the chip shows the *answer* wherever the page has one: a
   * composer that knows the couple is called Emma & Liam should say so, rather
   * than making the planner merge it in their head to check the sentence
   * reads. It stays a bubble, because what it will be is still a field and
   * not something she typed — and it keeps its name on hover, since a filled
   * chip has nowhere else to say which variable it is. A variable that varies
   * per message — every `vendor_` one — has no single answer here and keeps
   * its name; so does one this wedding has not filled in, which the blanks
   * line under the letter is already about.
   */
  addNodeView() {
    const options = this.options;
    return ({ node }) => {
      const name = node.attrs.name as string;
      const resolved = options.resolve?.(name)?.trim();
      const dom = document.createElement('span');
      dom.className = cn(CHIP_CLASS, variableChipClass(name));
      dom.setAttribute('data-variable', name);
      dom.textContent = resolved || name;
      if (resolved) dom.title = variableLabel(name);
      return { dom };
    };
  },

  /** What the node contributes to `editor.getText()`. Kept identical to the
   *  html form so a text and an html reading of the same document agree. */
  renderText({ node }) {
    return `{{${node.attrs.name}}}`;
  },

  addInputRules() {
    return [
      // Typing the token by hand still produces a chip, so a planner who knows
      // the syntax is not punished for using it — and, more importantly, does
      // not end up with a hand-typed token that formatting can later split.
      nodeInputRule({
        find: /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/,
        type: this.type,
        getAttributes: (match) => ({ name: match[1] }),
      }),
    ];
  },
});

/**
 * An image that carries the geometry a mail client needs.
 *
 * `width` as an attribute rather than only in CSS: Outlook ignores a great deal
 * of CSS and will render a 1600px image at 1600px, blowing the message out of
 * its column on every screen.
 */
const BodyImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('width'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.width ? { width: String(attributes.width) } : {},
      },
      style: {
        default: 'max-width: 100%;',
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.style ? { style: attributes.style as string } : {},
      },
    };
  },

  /**
   * The picture, with a size a planner can change.
   *
   * A look-book photograph and a floor-plan detail want different widths in
   * the same message, and the drop can only guess. So the selected image
   * carries a corner grip — the gesture every editor has trained for this —
   * and nothing else: a picture being resized is a direct-manipulation moment,
   * and a row of buttons parked on the photograph is chrome in front of the
   * thing being looked at.
   *
   * The drag writes nothing until it ends. Live feedback is the element's own
   * style; the document is touched once, on release, so a resize is one undo
   * rather than ninety. What gets written is the `width` *attribute* — the
   * geometry Outlook actually honours — which is what this node has always
   * serialized.
   */
  addNodeView() {
    return ({ node, editor, getPos }) => {
      const dom = document.createElement('div');
      dom.className = 'body-image';
      dom.draggable = true;

      const img = document.createElement('img');

      /** The widest this image may go: its own pixels, never past the column. */
      const ceiling = () => Math.min(img.naturalWidth || DISPLAY_WIDTH, DISPLAY_WIDTH);
      const clamp = (px: number) => Math.round(Math.max(MIN_IMAGE_WIDTH, Math.min(px, ceiling())));
      const current = () => Number(node.attrs.width) || img.getBoundingClientRect().width;

      const commit = (px: number) => {
        if (typeof getPos !== 'function') return;
        const pos = getPos();
        if (pos == null) return;
        editor.view.dispatch(
          editor.view.state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            width: clamp(px),
          }),
        );
      };

      const readout = document.createElement('span');
      readout.className = 'body-image__readout';
      readout.contentEditable = 'false';

      const grip = document.createElement('span');
      grip.className = 'body-image__grip';
      grip.setAttribute('aria-hidden', 'true');
      grip.draggable = false;

      grip.addEventListener('pointerdown', (event: PointerEvent) => {
        // The grip resizes; it must not also start ProseMirror's drag of the
        // node it sits on, which would carry the image away mid-gesture.
        event.preventDefault();
        event.stopPropagation();
        grip.setPointerCapture?.(event.pointerId);

        const startX = event.clientX;
        const startWidth = current();
        dom.classList.add('is-resizing');

        const move = (moveEvent: PointerEvent) => {
          const next = clamp(startWidth + (moveEvent.clientX - startX));
          img.style.width = `${next}px`;
          readout.textContent = `${next}px`;
        };
        const end = (endEvent: PointerEvent) => {
          grip.releasePointerCapture?.(endEvent.pointerId);
          grip.removeEventListener('pointermove', move);
          grip.removeEventListener('pointerup', end);
          grip.removeEventListener('pointercancel', end);
          dom.classList.remove('is-resizing');
          commit(clamp(startWidth + (endEvent.clientX - startX)));
        };

        grip.addEventListener('pointermove', move);
        grip.addEventListener('pointerup', end);
        grip.addEventListener('pointercancel', end);
      });


      const paint = (attrs: Record<string, unknown>) => {
        img.setAttribute('src', String(attrs.src ?? ''));
        img.setAttribute('alt', String(attrs.alt ?? ''));
        // The attribute as well as the style: it is the geometry the message
        // actually ships with, and drawing it here keeps what is on screen
        // and what is serialized the same object rather than two readings.
        if (attrs.width) {
          img.setAttribute('width', String(Number(attrs.width)));
          img.style.width = `${Number(attrs.width)}px`;
          readout.textContent = `${Number(attrs.width)}px`;
        } else {
          img.removeAttribute('width');
          img.style.width = '';
          readout.textContent = '';
        }
      };
      paint(node.attrs);

      dom.appendChild(img);
      dom.appendChild(readout);
      dom.appendChild(grip);

      return {
        dom,
        update: (updated) => {
          if (updated.type.name !== node.type.name) return false;
          node = updated;
          paint(updated.attrs);
          return true;
        },
        // The chrome is ours to mutate — a width written mid-drag is not the
        // document changing under ProseMirror's feet.
        ignoreMutation: () => true,
        stopEvent: (event) => grip.contains(event.target as globalThis.Node),
      };
    };
  },
});

/** The column a vendor reads in. Wider than this and the image is the message. */
const DISPLAY_WIDTH = 560;

/** Narrower than this and the picture is a thumbnail nobody can read. */
const MIN_IMAGE_WIDTH = 96;

/**
 * Wraps bare `{{tokens}}` in the markup a chip parses from.
 *
 * Runs on content coming in, not on content going out. A body written before
 * this editor existed — every template in the product on the day it ships —
 * holds its variables as literal text, and without this they would open as
 * punctuation while a newly typed one became a chip. Two kinds of variable in
 * one document, distinguishable only by their history, is worse than none.
 *
 * Text nodes only: a token that somehow appears inside a tag (an alt text, a
 * href) is left exactly as it is rather than being torn in half by a chip.
 */
/**
 * The inverse of `chipifyTokens`: chip spans back to the bare tokens they
 * wrap.
 *
 * Needed before a merge. `MergeVariable` parses *any* `span[data-variable]`
 * as an atom and renders it from the attribute, so a value merged into the
 * span's text — `<span data-variable="vendor_name">Amy Chen</span>` — comes
 * back into an editor as the chip "vendor_name" and serializes out as
 * `{{vendor_name}}` again. A planner who opened that message and fixed a
 * comma would un-merge it. Stripping the wrapper first means the merge
 * replaces plain text, and what is left unresolved is a plain token the
 * editor re-chips on the way in.
 */
export function stripVariableChips(html: string): string {
  return html.replace(
    /<span\b[^>]*\bdata-variable="[^"]*"[^>]*>([\s\S]*?)<\/span>/g,
    '$1',
  );
}

export function chipifyTokens(html: string): string {
  const CHIP_OPEN = /^<span[^>]*\bdata-variable=/i;
  let insideChip = false;

  return html
    .split(/(<[^>]*>)/)
    .map((segment) => {
      if (segment.startsWith('<')) {
        if (CHIP_OPEN.test(segment)) insideChip = true;
        else if (insideChip && /^<\/span/i.test(segment)) insideChip = false;
        return segment;
      }

      // The token *inside* an existing chip is already spoken for. Wrapping it
      // again nests a chip in a chip, and this function runs on the editor's
      // own output every time the value changes underneath it — so a
      // non-idempotent version would grow another layer on every template
      // switch and every reset.
      if (insideChip) return segment;

      return segment.replace(
        /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
        (_match, name: string) => `<span data-variable="${name}">{{${name}}}</span>`,
      );
    })
    .join('');
}

/** Every image already in the document, for the per-body limits. */
function imageStats(html: string): { count: number } {
  return { count: (html.match(/<img\b/gi) ?? []).length };
}

/**
 * Which surface the editor is drawn on.
 *
 * `field` is a form control: a bordered, filled box among other boxes.
 * `envelope` is the body of the letter the composer writes — the white page of
 * the previewed mail *is* the box, and a second bordered one inside it reads
 * as a textarea pasted into a letter. The variant un-sets the border, the
 * radius and the fill; the caret and the bottom toolbar are what say the page
 * can be typed in. Same vocabulary as `VendorRecipientField` and
 * `VariableInput` use for the To and subject lines above it.
 */
export type RichBodyEditorVariant = 'field' | 'envelope';

const BOX_CLASS: Record<RichBodyEditorVariant, string> = {
  field:
    'rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] focus-within:bg-[var(--color-surface-bright)]',
  // `rich-body--letter` is the paragraph rhythm of the sent mail; see app.css.
  envelope: 'rich-body--letter bg-transparent',
};

export interface RichBodyEditorProps {
  /** Sanitized html. Convert a legacy plain-text body with `plainTextToHtml`
   *  before it gets here — this editor has one input format. */
  value: string;
  onChange: (html: string) => void;
  id?: string;
  ariaLabel: string;
  placeholder?: string;
  /** The variable palette. Off in the reply box, where a reply is prose to one
   *  vendor and there is nothing to merge into it. */
  variables?: boolean;
  className?: string;
  minHeight?: string;
  /**
   * Where the formatting row sits.
   *
   * `top` is the form-field shape: controls announce themselves above a field
   * that is one of several on a page of them. `bottom` is the mail-client
   * shape — the box *is* the message, so the row that acts on it sits under
   * what was written, where every composer a planner already uses puts it.
   */
  toolbar?: 'top' | 'bottom' | 'none';
  /** Which surface this is drawn on — a form field, or the body of a letter.
   *  See `RichBodyEditorVariant`. */
  variant?: RichBodyEditorVariant;
  /**
   * What the page already knows about a merge variable, for the chips to show
   * in place of their own names. Return `undefined` for anything that has no
   * single answer here — a per-message field, or one nobody has filled in —
   * and the chip keeps its name. Display only: what the editor serializes is
   * the literal `{{token}}` either way.
   */
  resolveVariable?: (name: string) => string | undefined;
  /**
   * What `value` and `onChange` speak.
   *
   * `html` is the message body. `text` is a one-line field — the composer's
   * subject — whose stored form is plain text with literal `{{tokens}}` in it.
   * The editor is the same either way, which is the point: a subject line and
   * a body that chip and resolve their variables differently would be two
   * answers to one question on one letter.
   */
  output?: 'html' | 'text';
  /** One line, no paragraphs: Enter is refused rather than splitting a field
   *  that is a single line by definition. */
  singleLine?: boolean;
  /**
   * What the palette drops in: a chip, or the answer as prose.
   *
   * On a message that is still a template for a batch, a variable is a
   * placeholder and has to stay one — its value is different for every vendor
   * it goes to. On a message that has already been merged for one vendor,
   * there is nothing left to defer: the planner adding the date to *this*
   * letter means the date, and a bubble sitting in finished prose claims the
   * line is still waiting for something. Anything the page cannot answer —
   * `{{opening}}`, a field nobody filled in — is inserted as a chip either
   * way, because then it genuinely is still waiting.
   */
  insertAsValue?: boolean;
  /** The committing action, rendered first in the toolbar row. Only a bottom
   *  toolbar has anywhere sensible to put it. */
  actions?: React.ReactNode;
  /** Rendered at the far end of the toolbar row, away from `actions` — the
   *  discarding half of the pair, which must not sit next to Send. */
  trailing?: React.ReactNode;
  /** Shown inside the box, under what was typed: content that ships with the
   *  message but is not edited here. */
  children?: React.ReactNode;
}

export default function RichBodyEditor({
  value,
  onChange,
  id,
  ariaLabel,
  placeholder,
  variables = true,
  className,
  minHeight = 'min-h-[16rem]',
  toolbar = 'top',
  variant = 'field',
  resolveVariable,
  output = 'html',
  singleLine = false,
  insertAsValue = false,
  actions,
  trailing,
  children,
}: RichBodyEditorProps) {
  const [uploading, setUploading] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Read through a ref, because the extension below is configured once when
   *  the editor is built and the resolver arrives — and changes — later. */
  const resolveRef = useRef(resolveVariable);
  resolveRef.current = resolveVariable;
  const resolve = useRef((name: string) => resolveRef.current?.(name)).current;
  /** Bytes already accepted into this body, so the per-message ceiling is
   *  enforced on what was actually uploaded rather than on a re-measurement. */
  const uploadedBytes = useRef(0);

  /** The document in the caller's own format. `getText()` runs merge
   *  variables back through `renderText`, so a token survives the round trip
   *  as the literal `{{token}}` the merge is written against. */
  const read = useCallback(
    (instance: { isEmpty: boolean; getText: () => string; getHTML: () => string }) =>
      instance.isEmpty ? '' : output === 'text' ? instance.getText() : instance.getHTML(),
    [output],
  );
  const write = useCallback(
    (next: string) => chipifyTokens(output === 'text' ? plainTextToHtml(next) : next),
    [output],
  );

  const editor = useEditor({
    extensions: [
      // Link and underline come from StarterKit rather than as separate
      // extensions: registering either one twice is a duplicate-name warning
      // and two competing schema entries for the same mark.
      StarterKit.configure({
        // Every one of these renders to a tag the server sanitizer discards, so
        // enabling them would let a planner build something that silently
        // disappears on save.
        codeBlock: false,
        code: false,
        horizontalRule: false,
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true },
      }),
      BodyImage.configure({ inline: false, allowBase64: false }),
      MergeVariable.configure({ resolve }),
    ],
    content: write(value),
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        role: 'textbox',
        'aria-label': ariaLabel,
        'aria-multiline': 'true',
        // Typography comes from `.rich-body .ProseMirror` in app.css, shared
        // with the preview's `.email-body` so the two cannot drift.
        class: cn(
          'max-w-none text-sm leading-6 outline-none',
          // A one-line field is laid out by its caller — the envelope's row
          // already carries the letter's gutter — so it sits tight inside it,
          // on the same metrics as the To field beside it.
          singleLine ? 'px-1.5 py-0.5' : 'px-4 py-3',
          minHeight,
        ),
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },

      // A one-line field has nowhere for a second paragraph to go, and a
      // subject that silently grew one would be stored with a newline in it.
      handleKeyDown: singleLine
        ? (_view, event) => event.key === 'Enter' && !event.isComposing
        : undefined,

      /**
       * A drop lands where the pointer is, not at the end. That is the whole
       * difference between "put a picture in my message" and "attach a file",
       * and it is why the drop position is resolved before any awaiting starts
       * — by the time the upload returns, the cursor has moved on.
       */
      handleDrop: (view, event) => {
        // A one-line field has no room for a picture and no business holding
        // one: the subject of an email is text.
        if (singleLine) return false;
        const files = imageFilesFrom(event.dataTransfer?.files);
        if (files.length === 0) return false;

        event.preventDefault();
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        void insertImages(files, at);
        return true;
      },

      handlePaste: (_view, event) => {
        if (singleLine) return false;
        const files = imageFilesFrom(event.clipboardData?.files);
        if (files.length === 0) return false;

        event.preventDefault();
        void insertImages(files);
        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      // An empty document still serializes to `<p></p>`; empty means empty, so
      // the caller is not handed a body that looks blank and is not.
      onChange(read(instance));
    },
  });

  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  /**
   * Pull in a value the page changed underneath us — a template switch, a
   * "reset to the template" button, a query resolving after first render.
   * Compared against the editor's own output first, or every keystroke would
   * round-trip through the parent and reset the document, taking the cursor
   * with it.
   */
  useEffect(() => {
    if (!editor) return;
    if (value === read(editor)) return;
    editor.commands.setContent(write(value), { emitUpdate: false });
  }, [editor, value, read, write]);

  /**
   * Redraw the chips when the answers arrive.
   *
   * A node view renders once and is not rebuilt by a state change it is not
   * part of, so chips drawn before the wedding query settled would keep their
   * token names forever. Re-setting the editor's own html rebuilds them
   * against the new resolver and changes nothing else. Skipped while the
   * planner is typing: `setContent` moves the cursor, and the answers always
   * land long before she does.
   */
  useEffect(() => {
    const instance = editorRef.current;
    if (!instance || instance.isFocused) return;
    instance.commands.setContent(write(read(instance)), { emitUpdate: false });
  }, [resolveVariable, read, write]);

  const insertImages = useCallback(async (files: File[], at?: number) => {
    const instance = editorRef.current;
    if (!instance) return;

    const already = imageStats(instance.getHTML()).count;
    const room = MESSAGE_BODY_MAX_IMAGES - already;
    if (room <= 0) {
      toast.error(`A message can hold ${MESSAGE_BODY_MAX_IMAGES} images. Remove one first.`);
      return;
    }
    if (files.length > room) {
      toast.error(
        `Only ${room} more ${room === 1 ? 'image fits' : 'images fit'} in this message.`,
      );
    }

    let position = at;
    let resizedAny = false;

    for (const file of files.slice(0, room)) {
      setUploading((n) => n + 1);
      try {
        const prepared = await prepareImageForEmail(file);
        resizedAny ||= prepared.resized;

        if (uploadedBytes.current + prepared.file.size > MESSAGE_BODY_MAX_IMAGE_BYTES) {
          toast.error(
            'This message is getting too heavy to email. Remove an image before adding another.',
          );
          break;
        }

        const url = await uploadAsset(prepared.file);
        uploadedBytes.current += prepared.file.size;

        const width = Math.min(prepared.width, DISPLAY_WIDTH);
        const chain = instance.chain().focus();
        // Successive images stack downward from the drop point rather than
        // landing in reverse order on top of each other.
        if (position !== undefined) chain.setTextSelection(position);
        chain.setImage({ src: url, alt: '', width }).run();
        if (position !== undefined) position = instance.state.selection.to;
      } catch (err) {
        toast.error(
          err instanceof ImageRejected || err instanceof Error
            ? err.message
            : 'That image could not be added.',
        );
      } finally {
        setUploading((n) => n - 1);
      }
    }

    // Said once per drop rather than once per image, and only when something
    // actually changed: a planner who drops six photographs does not need six
    // toasts telling them the same thing.
    if (resizedAny) toast.success('Resized for email.');
  }, []);

  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      bold: instance?.isActive('bold') ?? false,
      italic: instance?.isActive('italic') ?? false,
      underline: instance?.isActive('underline') ?? false,
      bulletList: instance?.isActive('bulletList') ?? false,
      orderedList: instance?.isActive('orderedList') ?? false,
      link: instance?.isActive('link') ?? false,
    }),
  });

  const insertVariable = (name: string) => {
    if (!editor) return;
    const value = insertAsValue ? resolveRef.current?.(name)?.trim() : undefined;
    if (value) {
      // As text, through ProseMirror's own insertion rather than as html, so
      // an ampersand in a venue name stays an ampersand.
      editor.chain().focus().insertContent(value).run();
      return;
    }
    editor.chain().focus().insertContent({ type: 'mergeVariable', attrs: { name } }).run();
  };

  const toggleLink = () => {
    if (!editor) return;
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const href = window.prompt('Link to');
    if (!href) return;
    editor.chain().focus().setLink({ href }).run();
  };

  const bottom = toolbar === 'bottom';
  const noToolbar = toolbar === 'none';

  const toolbarRow = (
    <div
      className={cn(
        'flex flex-wrap items-center gap-0.5',
        // No rule under a bottom row. What separates it from the message is the
        // message ending, which is how every mail client draws this; a second
        // horizontal line inside an already-bordered box is one more than the
        // shape needs.
        bottom
          // Padded to the body's own gutter, so the send button's edge lands on
          // the same line the message starts at.
          ? 'gap-x-1 px-4 pt-1 pb-3'
          : 'border-b border-[var(--color-outline-variant)] px-2 py-1.5',
      )}
    >
      {/* Send leads the row, ahead of the formatting it commits — the reading
          order of a composer, where the last thing done is the first thing
          reached for. */}
      {actions && <div className="mr-1.5 flex items-center gap-2">{actions}</div>}

      <ToolbarButton label="Bold" active={state?.bold} onClick={() => editor?.chain().focus().toggleBold().run()}>
        <BoldIcon className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton label="Italic" active={state?.italic} onClick={() => editor?.chain().focus().toggleItalic().run()}>
        <ItalicIcon className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton label="Underline" active={state?.underline} onClick={() => editor?.chain().focus().toggleUnderline().run()}>
        <UnderlineIcon className="size-3.5" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton label="Bulleted list" active={state?.bulletList} onClick={() => editor?.chain().focus().toggleBulletList().run()}>
        <List className="size-3.5" />
      </ToolbarButton>
      <ToolbarButton label="Numbered list" active={state?.orderedList} onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
        <ListOrdered className="size-3.5" />
      </ToolbarButton>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolbarButton label={state?.link ? 'Remove link' : 'Add link'} active={state?.link} onClick={toggleLink}>
        {state?.link ? <Link2Off className="size-3.5" /> : <Link2 className="size-3.5" />}
      </ToolbarButton>
      <ToolbarButton label="Add an image" onClick={() => fileInputRef.current?.click()}>
        <ImagePlus className="size-3.5" />
      </ToolbarButton>

      {uploading > 0 && (
        <span className="ml-1 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Spinner className="size-3" />
          Adding…
        </span>
      )}

      {/* The far end of the row, because discard and send are the two halves of
          one decision and must never be adjacent. */}
      {trailing && <div className="ml-auto flex items-center pl-2">{trailing}</div>}
    </div>
  );

  return (
    <div className={cn('space-y-2', className)}>
      <div className={cn('rich-body overflow-hidden', BOX_CLASS[variant])}>
        {!bottom && !noToolbar && toolbarRow}

        <EditorContent editor={editor} />

        {children}

        {bottom && !noToolbar && toolbarRow}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            // Cleared so choosing the same file twice in a row still fires.
            e.target.value = '';
            if (files.length) void insertImages(files);
          }}
        />
      </div>

      {variables && (
        <div
          className={cn(
            'space-y-1.5',
            // On the letter the palette is not a caption under a field — it is
            // a tray of parts below the message, and without a rule and some
            // air it reads as the last line of what was written.
            variant === 'envelope' && 'mt-4 border-t border-border px-4 pt-4 pb-1',
          )}
        >
          <p className="label-caps text-xs text-muted-foreground">
            Variables
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PALETTE.map((entry) => (
              <button
                key={entry.name}
                type="button"
                onClick={() => insertVariable(entry.name)}
                aria-label={`Insert ${entry.label}`}
                className="group inline-flex items-center gap-1 rounded-full bg-[var(--color-surface-container-high)] py-1 pr-3 pl-2 text-xs font-medium transition-colors hover:bg-[var(--color-primary-container)] hover:text-[var(--color-accent-foreground)]"
              >
                <Plus className="size-3 text-muted-foreground transition-colors group-hover:text-[var(--color-accent-foreground)]" />
                {entry.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ToolbarButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={cn('size-7 p-0', active && 'bg-[var(--color-surface-container-high)]')}
    >
      {children}
    </Button>
  );
}

function imageFilesFrom(list: FileList | null | undefined): File[] {
  return [...(list ?? [])].filter((f) => f.type.startsWith('image/'));
}

/**
 * Uploads one prepared image and returns the path the editor references it by.
 *
 * `fetch` rather than the Hono RPC client because the route takes multipart and
 * has no validator, so there is no typed shape for the client to offer. The
 * route's rejections are written for a planner to read and are surfaced
 * verbatim rather than translated into something vaguer.
 */
async function uploadAsset(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);

  const res = await fetch(`${API_BASE}/api/message-assets`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const body = (await res.json().catch(() => null)) as { error?: string; url?: string } | null;
  if (!res.ok || !body?.url) {
    throw new Error(body?.error ?? 'That image could not be added.');
  }
  return body.url;
}
